import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { X, Loader2, Truck, Info, AlertTriangle } from 'lucide-react';
// A factory, not a hook, despite the filename — imported under its real name so
// react-hooks/rules-of-hooks does not key on the identifier. See usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';

// Add / edit a distributor. One modal for both: the field set is identical and
// the only differences are the title and the endpoint.
//
// THIS FORM DECIDES WHETHER THE HOSPITAL CAN RECLAIM ITS INPUT TAX. Everything
// else on the purchase side is a document — an order, a receipt, a credit note —
// and can be cancelled and re-keyed. The supplier row underneath is the one
// thing every one of those documents quotes, so a mistake here is not corrected
// once, it is corrected on every purchase already filed against it.
//
// STATUS IS DELIBERATELY NOT ON THIS FORM. Retiring a distributor goes through
// DELETE /pharmacy/suppliers/:id, which refuses while an order is still open —
// letting a plain edit set status='inactive' would route straight around that
// check and leave it decorative, which is the usual way a guard stops being one.
// The server refuses it too (purchaseSupplierController.updateSupplier); leaving
// the field off the form means the user never meets that refusal. Reactivation
// is the reverse case and is safe, so the list screen does it with a PUT.

const EMPTY = {
    name: '', contactPerson: '', phone: '', email: '', address: '',
    gstin: '', drugLicenseNo: '', paymentTermDays: '30', openingBalance: '',
};

// The statutory GSTIN shape, mirroring GSTIN_RE in
// Backend/api/controllers/purchaseSupplierController.js: 2-digit state code,
// 10-character PAN, entity number, a literal 'Z', then a checksum character.
//
// CHECKED HERE AS WELL AS ON THE SERVER, and the duplication is the point. A
// mistyped GSTIN is not a data-quality blemish — input tax credit is matched
// against what the supplier filed in GSTR-2B, so every invoice from that party
// until somebody notices is tax the hospital has paid and can never reclaim. The
// server is the real gate; this copy exists so the buyer finds out while they
// are still looking at the number on the invoice in their hand, rather than
// after a round trip that only says "failed".
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;

// Deliberately permissive, matching the server: an address-book email is used to
// send a purchase order, and refusing an unusual but legal address means the
// buyer stops recording it at all. This rejects the typo classes ("no @",
// trailing comma, a space in the middle), not the exotic-but-valid ones.
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

// DECIMAL(12,2) holds up to 9,999,999,999.99. Anything larger is a keying error
// — a phone number pasted into the amount box — and letting it through surfaces
// as a Postgres numeric overflow, i.e. a 500 with a stack trace where the buyer
// needed a sentence naming the field.
const MAX_DECIMAL_12_2 = 9999999999.99;

// The first two digits of a GSTIN are the state of registration, and that is
// what decides CGST+SGST versus IGST on every goods receipt from this party.
// Named here rather than left as two digits because "27" tells a buyer nothing
// and "Maharashtra" tells them immediately whether the next delivery is an
// inter-state supply — which is the one field on a receipt nobody can fix
// afterwards without cancelling it.
//
// 25 and 28 are retired (Daman & Diu merged into 26 in 2020; undivided Andhra
// Pradesh became 37), but they still appear on registrations issued before then,
// so they are listed rather than rejected.
const GST_STATE_NAMES = {
    '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
    '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi',
    '08': 'Rajasthan', '09': 'Uttar Pradesh', 10: 'Bihar', 11: 'Sikkim',
    12: 'Arunachal Pradesh', 13: 'Nagaland', 14: 'Manipur', 15: 'Mizoram',
    16: 'Tripura', 17: 'Meghalaya', 18: 'Assam', 19: 'West Bengal',
    20: 'Jharkhand', 21: 'Odisha', 22: 'Chhattisgarh', 23: 'Madhya Pradesh',
    24: 'Gujarat', 25: 'Daman & Diu (retired)', 26: 'Dadra & Nagar Haveli and Daman & Diu',
    27: 'Maharashtra', 28: 'Andhra Pradesh (retired)', 29: 'Karnataka',
    30: 'Goa', 31: 'Lakshadweep', 32: 'Kerala', 33: 'Tamil Nadu',
    34: 'Puducherry', 35: 'Andaman & Nicobar Islands', 36: 'Telangana',
    37: 'Andhra Pradesh', 38: 'Ladakh', 97: 'Other Territory',
    99: 'Centre Jurisdiction',
};

const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

export default function AddSupplier({ isOpen, onClose, onSaved, editing = null }) {
    const { t } = useTranslation();
    // One memoised instance: it reads the access token inside its request
    // interceptor, so a single copy stays correct across a token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const isEdit = Boolean(editing);

    useEffect(() => {
        if (!isOpen) return;
        setErrors({});
        setSaving(false);
        if (editing) {
            setForm({
                name: editing.name || '',
                contactPerson: editing.contactPerson || '',
                phone: editing.phone || '',
                email: editing.email || '',
                address: editing.address || '',
                gstin: editing.gstin || '',
                drugLicenseNo: editing.drugLicenseNo || '',
                // Null-safe rather than `|| '30'`: a supplier genuinely on cash
                // terms has paymentTermDays 0, and the falsy fallback would
                // quietly re-grant them a month's credit on any unrelated edit.
                paymentTermDays:
                    editing.paymentTermDays === null || editing.paymentTermDays === undefined
                        ? '30'
                        : String(editing.paymentTermDays),
                openingBalance:
                    editing.openingBalance === null || editing.openingBalance === undefined
                        ? ''
                        : String(editing.openingBalance),
            });
        } else {
            setForm(EMPTY);
        }
    }, [isOpen, editing]);

    if (!isOpen) return null;

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const gstin = form.gstin.trim().toUpperCase();
    const gstinValid = GSTIN_RE.test(gstin);
    const stateCode = gstinValid ? gstin.slice(0, 2) : null;
    const stateName = stateCode ? GST_STATE_NAMES[stateCode] || null : null;

    const validate = () => {
        const e = {};

        if (!form.name.trim()) {
            e.name = t('supplierpage.name_required', 'Name is required');
        }

        if (gstin && !gstinValid) {
            e.gstin = t(
                'supplierpage.gstin_invalid',
                'That is not a valid GST number (15 characters, e.g. 29ABCDE1234F1Z5). Input tax credit is matched on it — leave it blank rather than store a guess.'
            );
        }

        const email = form.email.trim();
        if (email && !EMAIL_RE.test(email)) {
            e.email = t('supplierpage.email_invalid', 'That is not a valid email address');
        }

        // Whole days only. Nobody agrees credit terms in half a day, and a
        // fractional value truncates in the INTEGER column — so the ageing bucket
        // the cheque run is prepared from would not be the one that was agreed.
        // 365 is a typo catch rather than a real ceiling: "3000" is a mis-keyed 30.
        const days = Number(form.paymentTermDays);
        if (form.paymentTermDays === '' || !Number.isInteger(days) || days < 0 || days > 365) {
            e.paymentTermDays = t(
                'supplierpage.terms_invalid',
                'Payment terms must be a whole number of days between 0 and 365'
            );
        }

        if (form.openingBalance !== '') {
            const balance = Number(form.openingBalance);
            if (!Number.isFinite(balance) || Math.abs(balance) > MAX_DECIMAL_12_2) {
                e.openingBalance = t('supplierpage.opening_invalid', 'Opening balance must be an amount');
            }
            // A NEGATIVE FIGURE IS LEGITIMATE AND IS NOT VALIDATED AWAY: an
            // advance paid to a distributor before go-live means they owe the
            // hospital, not the other way round. Refusing it would push that
            // figure into a fake purchase invoice for goods that were never
            // received, which is far worse than a minus sign.
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;

        const payload = {
            name: form.name.trim(),
            contactPerson: form.contactPerson.trim() || null,
            phone: form.phone.trim() || null,
            email: form.email.trim() || null,
            address: form.address.trim() || null,
            // Upper-cased before it leaves, because this value is compared
            // against what the supplier printed on their invoice. Storing
            // '29abcde1234f1z5' fails every one of those comparisons while
            // looking correct to a human reading the screen.
            gstin: gstin || null,
            drugLicenseNo: form.drugLicenseNo.trim().toUpperCase() || null,
            paymentTermDays: Number(form.paymentTermDays),
            openingBalance: form.openingBalance === '' ? 0 : Number(form.openingBalance),
        };

        setSaving(true);
        try {
            if (isEdit) {
                await axiosInstance.put(`/pharmacy/suppliers/${editing.supplierId}`, payload);
                toast.success(t('supplierpage.update_success', 'Supplier updated'));
            } else {
                const res = await axiosInstance.post('/pharmacy/suppliers', payload);
                toast.success(t('supplierpage.create_success', 'Supplier added'));
                // A SECOND ROW UNDER ONE GSTIN IS REPORTED, NEVER REFUSED — one
                // registered entity legitimately trades under more than one name
                // (a depot and a head office share a GSTIN). The server returns
                // 201 with a `warning` for exactly that reason, and swallowing it
                // here would waste the only moment somebody can check.
                const warning = res?.data?.warning;
                if (warning?.message) toast.warn(warning.message, { autoClose: 8000 });
            }
            onSaved?.();
            onClose?.();
        } catch (err) {
            // The duplicate-name 409 is about ONE field and carries a sentence
            // explaining the consequence (a payables ledger split in half), so it
            // is pinned to that field rather than flashed past in a toast the
            // buyer has to remember while retyping.
            if (err?.response?.status === 409 && err?.response?.data?.code === 'SUPPLIER_DUPLICATE_NAME') {
                setErrors((e) => ({ ...e, name: err.response.data.message }));
                return;
            }
            toast.error(apiError(err, t('supplierpage.save_error', 'Failed to save the supplier')));
        } finally {
            setSaving(false);
        }
    };

    const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const label = 'text-xs font-medium text-slate-500';

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="add-supplier-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-[#4ABDBD]/10 flex items-center justify-center">
                            <Truck className="w-4 h-4 text-[#2D8A8A]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {isEdit
                                    ? t('supplierpage.edit_supplier', 'Edit supplier')
                                    : t('supplierpage.add_supplier', 'Add supplier')}
                            </h2>
                            {isEdit && <p className="text-[11px] text-slate-400 font-mono">{editing.supplierId}</p>}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="add-supplier-close"
                        className="text-gray-400 hover:text-gray-600 cursor-pointer"
                        aria-label={t('supplierpage.close', 'Close')}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                        <label className={label}>{t('supplierpage.name', 'Distributor name')} *</label>
                        <input
                            data-testid="add-supplier-name"
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            maxLength={150}
                            className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.name
                            ? <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                            : (
                                // Not house style — a real failure mode. Two rows
                                // for one distributor split the payables ledger in
                                // half; neither half looks wrong on its own, and
                                // the overdue half is the one nobody is watching.
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('supplierpage.name_hint', 'One row per distributor. A second row for the same party splits their payables ledger in two.')}
                                </p>
                            )}
                    </div>

                    <div>
                        <label className={label}>{t('supplierpage.contact_person', 'Contact person')}</label>
                        <input
                            data-testid="add-supplier-contact"
                            value={form.contactPerson}
                            onChange={(e) => set('contactPerson', e.target.value)}
                            maxLength={120}
                            className={`${field} border-gray-300`}
                        />
                    </div>
                    <div>
                        <label className={label}>{t('supplierpage.phone', 'Phone')}</label>
                        <input
                            data-testid="add-supplier-phone"
                            value={form.phone}
                            onChange={(e) => set('phone', e.target.value)}
                            maxLength={30}
                            inputMode="tel"
                            className={`${field} border-gray-300`}
                        />
                    </div>

                    <div>
                        <label className={label}>{t('supplierpage.email', 'Email')}</label>
                        <input
                            data-testid="add-supplier-email"
                            value={form.email}
                            onChange={(e) => set('email', e.target.value)}
                            maxLength={150}
                            inputMode="email"
                            className={`${field} ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.email
                            ? <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                            : <p className="text-[11px] text-slate-400 mt-1">{t('supplierpage.email_hint', 'Where a purchase order is sent.')}</p>}
                    </div>
                    <div>
                        <label className={label}>{t('supplierpage.payment_terms', 'Payment terms (days)')}</label>
                        <input
                            data-testid="add-supplier-terms"
                            type="number"
                            min="0"
                            max="365"
                            step="1"
                            value={form.paymentTermDays}
                            onChange={(e) => set('paymentTermDays', e.target.value)}
                            className={`${field} ${errors.paymentTermDays ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.paymentTermDays
                            ? <p className="text-xs text-red-500 mt-1">{errors.paymentTermDays}</p>
                            : (
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('supplierpage.terms_hint', 'Credit agreed with this distributor — 0 for cash on delivery. It sets the ageing bucket their invoices fall into.')}
                                </p>
                            )}
                    </div>

                    <div className="sm:col-span-2">
                        <label className={label}>{t('supplierpage.address', 'Address')}</label>
                        <textarea
                            data-testid="add-supplier-address"
                            value={form.address}
                            onChange={(e) => set('address', e.target.value)}
                            rows={2}
                            maxLength={500}
                            className={`${field} border-gray-300 resize-none`}
                        />
                    </div>

                    {/* ---- Statutory identity. This is the half that costs money. ---- */}
                    <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <h3 className="text-sm font-bold text-[#2D8A8A]">
                                {t('supplierpage.statutory_section', 'Registration and licence')}
                            </h3>
                            <span className="text-[11px] text-slate-400">
                                {t('supplierpage.statutory_hint', 'Quoted on every purchase invoice this distributor ever raises on the hospital.')}
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className={label}>{t('supplierpage.gstin', 'GSTIN')}</label>
                        <input
                            data-testid="add-supplier-gstin"
                            value={form.gstin}
                            // Upper-cased and stripped as it is typed rather than on
                            // save, so the state code below can appear the moment the
                            // fifteenth character lands and the buyer can check it
                            // against the invoice in front of them.
                            onChange={(e) => set('gstin', e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15))}
                            maxLength={15}
                            placeholder="29ABCDE1234F1Z5"
                            className={`${field} font-mono tracking-wide ${errors.gstin ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.gstin && <p className="text-xs text-red-500 mt-1">{errors.gstin}</p>}
                        {!errors.gstin && gstinValid && (
                            <p data-testid="add-supplier-state-code" className="text-[11px] text-[#2D8A8A] mt-1 flex items-start gap-1 font-semibold">
                                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {t('supplierpage.state_code', 'Registered in {{state}} ({{code}}) — this is what decides CGST + SGST or IGST on every receipt from them.', {
                                    state: stateName || t('supplierpage.unknown_state', 'an unrecognised state code'),
                                    code: stateCode,
                                })}
                            </p>
                        )}
                        {!errors.gstin && !gstinValid && (
                            <p className="text-[11px] text-slate-400 mt-1">
                                {t('supplierpage.gstin_hint', 'Input tax credit is matched against this number. Blank is safer than a guess — a wrong one is tax the hospital pays and can never reclaim.')}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className={label}>{t('supplierpage.drug_licence', 'Drug licence number')}</label>
                        <input
                            data-testid="add-supplier-licence"
                            value={form.drugLicenseNo}
                            onChange={(e) => set('drugLicenseNo', e.target.value.toUpperCase().slice(0, 60))}
                            maxLength={60}
                            placeholder="KA-B-20/21A"
                            className={`${field} border-gray-300 font-mono`}
                        />
                        {/* A drug inspector asks the pharmacy to show which
                            LICENSED dealer any stock on its shelf came from. The
                            licence number is the answer, and the moment to record
                            it is before the first order — not when it is asked
                            for. */}
                        <p className="text-[11px] text-slate-400 mt-1">
                            {t('supplierpage.licence_hint', 'The wholesale licence the hospital buys under. An inspector asks which licensed dealer the stock came from.')}
                        </p>
                    </div>

                    <div className="sm:col-span-2">
                        <label className={label}>{t('supplierpage.opening_balance', 'Opening balance (₹)')}</label>
                        <input
                            data-testid="add-supplier-opening"
                            type="number"
                            step="0.01"
                            value={form.openingBalance}
                            onChange={(e) => set('openingBalance', e.target.value)}
                            placeholder="0.00"
                            className={`${field} sm:max-w-xs ${errors.openingBalance ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {errors.openingBalance
                            ? <p className="text-xs text-red-500 mt-1">{errors.openingBalance}</p>
                            : (
                                // THE ONLY SUPPLIER BALANCE ANYBODY MAY TYPE. Every
                                // other figure on their account is derived from a
                                // document — a posted receipt or a settled credit
                                // note — and can only be changed by cancelling one.
                                // It stays editable after go-live because finance
                                // corrects it once they have reconciled with the
                                // distributor, and it is not derived from anything.
                                <p className="text-[11px] text-slate-400 mt-1 flex items-start gap-1">
                                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#4ABDBD]" />
                                    {t('supplierpage.opening_hint', 'What was owed on the day this system went live. Positive means the hospital owes them; use a minus for an advance already paid. Every later figure comes from a receipt or a credit note.')}
                                </p>
                            )}
                    </div>

                    {isEdit && (
                        <div className="sm:col-span-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {t('supplierpage.edit_note', 'Renaming a distributor changes how they read on every purchase already filed against them — the documents themselves are untouched. To stop buying from them, retire them from the list instead; that checks for orders they still owe first.')}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('supplierpage.cancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        data-testid="add-supplier-submit"
                        className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isEdit ? t('supplierpage.save', 'Save changes') : t('supplierpage.create', 'Add supplier')}
                    </button>
                </div>
            </div>
        </div>
    );
}
